import type { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import { useAbsenceTypes, useActivityTypeGroups } from '@src/features/absence';
import { useFormContext } from '@src/shared/form';
import { AutocompleteField } from '@src/shared/ui/fields';
import { List, ListItem } from '@src/shared/ui/List';
import Box from '@src/shared/ui/layout/Box';
import Typography from '@src/shared/ui/Typography';
import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ScheduleFormValues } from '../Form';
import { StyleSheet } from './TypeField.styles';

type TypeFieldProps = {
  disabled?: boolean;
};

const UNGROUPED = '';

export default function TypeField({ disabled = false }: TypeFieldProps) {
  const { watch } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  const userId = watch('userId');

  const { absenceTypes } = useAbsenceTypes({ editableForUserId: userId });
  const { activityTypeGroups } = useActivityTypeGroups();

  const groupsMap = useMemo(
    () => new Map(activityTypeGroups.map((g) => [g.id, g])),
    [activityTypeGroups],
  );

  const options = useMemo(() => {
    const items = absenceTypes.map((item) => {
      const group = item.groupId != null ? groupsMap.get(item.groupId) : null;
      return {
        label: item.name,
        value: item.id,
        group: group?.name ?? UNGROUPED,
      };
    });
    return items.sort((a, b) => b.group.localeCompare(a.group));
  }, [absenceTypes, groupsMap]);

  const renderGroup = useMemo(() => {
    const groupStyleMap = new Map(
      activityTypeGroups.map((g) => [
        g.name,
        { fg: g.colourFg, bg: g.colourBg },
      ]),
    );

    return (params: AutocompleteRenderGroupParams) => {
      const colours = groupStyleMap.get(params.group);
      return (
        <ListItem key={params.key} disablePadding>
          <Box sx={StyleSheet.groupContainer}>
            {params.group !== UNGROUPED && (
              <Typography
                sx={StyleSheet.groupHeader(
                  colours?.fg ?? null,
                  colours?.bg ?? null,
                )}
              >
                {params.group}
              </Typography>
            )}
            <List disablePadding>{params.children}</List>
          </Box>
        </ListItem>
      );
    };
  }, [activityTypeGroups]);

  return (
    <AutocompleteField
      disabled={disabled}
      label="Absence type"
      name="typeId"
      options={options}
      groupBy={(option: (typeof options)[number]) => option.group}
      renderGroup={renderGroup}
      required
    />
  );
}
